import { AuthService } from './authService';
import { StcBinDto, StcBinPagedDto, StcBinsPagedQueryParams, PagedResult, CreateStcBinRequest, UpdateStcBinRequest, ExtendShelfResponse } from '../types/stcBin';
import { FieldRegistrationSummary } from '../types/fieldRegistration';
import {
  RackSummary,
  RackStructure,
  CreateRackRequest,
  CreateRackResponse,
  ValidateRackRequest,
  ValidateRackResponse,
  FilterBinsRequest,
  BinLocation,
  FilterOptions,
  BulkUpdateBinsRequest,
  BulkOperationResponse,
  BulkDeleteBinsRequest
} from '../components/warehouse/modules/rack-builder/types';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface BinSizeAvailability {
  binSizeNo: number;
  binCount: number;
}

class StcBinService {
  
  /**
   * Get bin by company, warehouse and bin code
   */
  async getByCompanyWarehouseAndBinCode(companyNo: string, warehouseID: string, binCode: string): Promise<StcBinDto> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stc-bins/${companyNo}/${warehouseID}/${binCode}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get bin ${binCode}: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Get all bins for a company and warehouse
   */
  async getByCompanyAndWarehouse(companyNo: string, warehouseID: string): Promise<StcBinDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stc-bins/company/${companyNo}/warehouse/${warehouseID}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get bins for company ${companyNo} and warehouse ${warehouseID}: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Get all bins for a company
   */
  async getByCompanyNo(companyNo: string): Promise<StcBinDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stc-bins/company/${companyNo}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get bins for company ${companyNo}: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Get paginated bins with server-side filtering, sorting, and enrichment.
   */
  async getCurrentPaged(
    params: StcBinsPagedQueryParams = {}
  ): Promise<PagedResult<StcBinPagedDto>> {
    const queryParams = new URLSearchParams();

    // Pagination
    if (params.pageNumber !== undefined) {
      queryParams.append('pageNumber', params.pageNumber.toString());
    }
    if (params.pageSize !== undefined) {
      queryParams.append('pageSize', params.pageSize.toString());
    }

    // Sorting
    if (params.sortColumn) {
      queryParams.append('sortColumn', params.sortColumn);
    }
    if (params.sortDirection) {
      queryParams.append('sortDirection', params.sortDirection);
    }

    // Filtering — append all non-empty filter values
    const filterFields: (keyof StcBinsPagedQueryParams)[] = [
      'binCode', 'description1', 'warehouseID', 'stcZoneID',
      'binGrpNo', 'stcBinCatNo', 'binTypeNo', 'binSizeNo',
      'movementTypeNo', 'resourceNo', 'pickPriorityNo', 'putAwayPriorityNo',
      'customSortID', 'replenishMinLevel', 'replenishMaxLevel', 'sharedBinMaxQuantity',
      'pickCustomOrder', 'putAwayCustomOrder', 'itemNo',
      'pickableYesNo', 'reservationAllowed', 'autoReplenishment',
      'negativeReservationAllowed', 'sharedBinYesNo', 'sharedBinPreferEmptyBin',
      'lastCountedDate', 'lastMovementDate',
      // B3: flervalgs lookup-filter (IN)
      'binGrpNos', 'stcBinCatNos', 'binTypeNos', 'binSizeNos',
      'movementTypeNos', 'pickPriorityNos', 'putAwayPriorityNos',
      'resourceNos', 'warehouseIDs', 'stcZoneIDs',
    ];
    for (const field of filterFields) {
      const value = params[field];
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(field, String(value));
      }
    }

    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/stc-bins/current/paged${queryString ? `?${queryString}` : ''}`;

    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to get paged bins'));
    }

    return await response.json();
  }

  /**
   * Get all bins for current tenant
   */
  async getCurrent(): Promise<StcBinDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/stc-bins/current`);
    
    if (!response.ok) {
      throw new Error(`Failed to get current bins: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Get all bins
   */
  async getAll(): Promise<StcBinDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/stc-bins`);
    
    if (!response.ok) {
      throw new Error(`Failed to get all bins: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Create new bin
   */
  async create(request: CreateStcBinRequest): Promise<StcBinDto> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stc-bins`,
      request
    );
    
    if (!response.ok) {
      throw new Error(`Failed to create bin: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Update bin
   */
  async update(companyNo: string, warehouseID: string, binCode: string, request: UpdateStcBinRequest): Promise<StcBinDto> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/stc-bins/${companyNo}/${warehouseID}/${binCode}`,
      request
    );
    
    if (!response.ok) {
      throw new Error(`Failed to update bin ${binCode}: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Delete bin
   */
  async delete(companyNo: string, warehouseID: string, binCode: string): Promise<void> {
    const response = await AuthService.authenticatedDelete(
      `${API_BASE_URL}/stc-bins/${companyNo}/${warehouseID}/${binCode}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to delete bin ${binCode}: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Check if bin exists
   */
  async exists(companyNo: string, warehouseID: string, binCode: string): Promise<boolean> {
    const response = await AuthService.authenticatedFetch(
      `${API_BASE_URL}/stc-bins/${companyNo}/${warehouseID}/${binCode}`,
      { method: 'HEAD' }
    );
    
    return response.ok;
  }

  /**
   * Get bin by exact bin code (fast lookup without warehouseID)
   */
  async getByExactBinCode(companyNo: string, binCode: string): Promise<StcBinDto | null> {
    const startTime = performance.now();
    console.log('🔍 [stcBinService] Calling exact endpoint:', `${API_BASE_URL}/stc-bins/exact/${companyNo}/${binCode}`);

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stc-bins/exact/${companyNo}/${encodeURIComponent(binCode)}`
    );

    const endTime = performance.now();
    console.log(`⏱️ [stcBinService] Exact endpoint responded in ${Math.round(endTime - startTime)}ms, status: ${response.status}`);

    if (response.status === 404) {
      console.log('❌ [stcBinService] Bin not found (404)');
      return null; // Bin not found
    }

    if (!response.ok) {
      console.error('❌ [stcBinService] Request failed:', response.status, response.statusText);
      throw new Error(`Failed to get bin by exact code: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('✅ [stcBinService] Received bin data:', data);
    return data;
  }

  /**
   * Search bins
   */
  async search(companyNo: string, searchTerm: string): Promise<StcBinDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stc-bins/search/${companyNo}?searchTerm=${encodeURIComponent(searchTerm)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to search bins: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get available bins for receipt - filters out blocked bins and includes only receivable bins
   */
  async getAvailableBinsForReceipt(companyNo: string, warehouseID: string): Promise<StcBinDto[]> {
    const allBins = await this.getByCompanyAndWarehouse(companyNo, warehouseID);
    
    // Filter to only include bins that are suitable for receipt
    return allBins.filter(bin => 
      // Not blocked by various flags
      !bin.sharedBinYesNo || // Allow if not shared bin, or if shared bin is explicitly allowed
      bin.reservationAllowed !== false && // Allow if reservations are allowed
      bin.pickableYesNo !== false && // Allow if pickable (or undefined)
      bin.binCode && // Must have a bin code
      bin.binCode.trim() !== '' // Bin code must not be empty
    );
  }

  // ============================================================================
  // RACK BUILDER / WAREHOUSE BUILDER METHODS (FR-001 to FR-008)
  // ============================================================================

  /**
   * Get rack summary for warehouse (FR-001)
   */
  async getRackSummary(companyNo: string, warehouseID: string): Promise<RackSummary[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stc-bins/company/${companyNo}/warehouse/${warehouseID}/racks`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get rack summary: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Get field registration summary for warehouse (aggregated item/capacity counts)
   */
  /**
   * Antall lokasjoner per størrelse innenfor en lokasjonsgruppe.
   *
   * Størrelser UTEN lokasjoner i gruppen kommer ikke med i svaret — kalleren
   * behandler manglende størrelser som 0 (TW 36331134).
   */
  async getBinSizeAvailability(companyNo: string, binGrpNo: number): Promise<BinSizeAvailability[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stc-bins/company/${companyNo}/bin-group/${binGrpNo}/size-availability`
    );

    if (!response.ok) {
      throw new Error(`Failed to get bin size availability: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  async getFieldRegistrationSummary(companyNo: string, warehouseID: string): Promise<FieldRegistrationSummary> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stc-bins/company/${companyNo}/warehouse/${warehouseID}/field-registration-summary`
    );

    if (!response.ok) {
      throw new Error(`Failed to get field registration summary: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get rack structure (FR-002)
   *
   * Guard mot placeholder-verdier (`[X]`, `[?]`, tomme strenger osv.) som har
   * lekket fra RackBuilder/RackBuilder-Live default-state. Backend logget
   * tidligere 500-feil for hver slik request — vi avbryter klient-side i stedet.
   */
  async getRackStructure(companyNo: string, warehouseID: string, rackNumber: string): Promise<RackStructure> {
    const trimmed = (rackNumber ?? '').trim();
    if (!trimmed || /^\[.*\]$/.test(trimmed)) {
      throw new Error(`Invalid rackNumber: '${rackNumber}'`);
    }

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stc-bins/company/${companyNo}/warehouse/${warehouseID}/racks/${encodeURIComponent(trimmed)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get rack structure: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Create complete rack with all locations (FR-003)
   */
  async createRack(request: CreateRackRequest): Promise<CreateRackResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stc-bins/racks`,
      request
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(extractApiError(errorData, response, 'Failed to create rack'));
    }
    
    return await response.json();
  }

  /**
   * Validate rack before creation (FR-008)
   */
  async validateRack(request: ValidateRackRequest): Promise<ValidateRackResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stc-bins/racks/validate`,
      request
    );
    
    if (!response.ok) {
      throw new Error(`Failed to validate rack: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Filter bins by multiple criteria (FR-004)
   */
  async filterBins(request: FilterBinsRequest): Promise<BinLocation[]> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stc-bins/filter`,
      request
    );
    
    if (!response.ok) {
      throw new Error(`Failed to filter bins: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Get filter options for warehouse (FR-005)
   */
  async getFilterOptions(companyNo: string, warehouseID: string): Promise<FilterOptions> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stc-bins/company/${companyNo}/warehouse/${warehouseID}/filter-options`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to get filter options: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Bulk update multiple bins (FR-006)
   */
  async bulkUpdateBins(request: BulkUpdateBinsRequest): Promise<BulkOperationResponse> {
    const response = await AuthService.authenticatedPut(
      `${API_BASE_URL}/stc-bins/bulk-update`,
      request
    );
    
    if (!response.ok) {
      throw new Error(`Failed to bulk update bins: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Bulk delete multiple bins (FR-007)
   */
  async bulkDeleteBins(request: BulkDeleteBinsRequest): Promise<BulkOperationResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stc-bins/bulk-delete`,
      request
    );

    if (!response.ok) {
      throw new Error(`Failed to bulk delete bins: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  // ============================================================================
  // EXTEND SHELF — Utvid hylle med neste lokasjon
  // ============================================================================

  /**
   * Forhåndsvis hva som ville bli opprettet hvis hyllen utvides fra parent.
   * Oppretter INGENTING i databasen.
   *
   * Returnerer alltid en ExtendShelfResponse — feil-tilfeller kommer med
   * success=false og errorCode satt.
   */
  async previewExtendShelf(parentBinCode: string): Promise<ExtendShelfResponse> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stc-bins/extend-shelf/preview/${encodeURIComponent(parentBinCode)}`
    );

    // 200, 404, 409, 422 skal alle returnere ExtendShelfResponse-body
    // 500+ eller andre uventede statuser skal kaste
    if (response.status >= 500) {
      throw new Error(`Preview extend-shelf failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Utvider hyllen ved å opprette neste lokasjon i sekvensen.
   *
   * Returnerer ExtendShelfResponse med success=true og createdBin ved vellykket opprettelse,
   * eller success=false med errorCode ved validert feil (hylle full, kollisjon, etc).
   */
  async extendShelf(parentBinCode: string): Promise<ExtendShelfResponse> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/stc-bins/extend-shelf/${encodeURIComponent(parentBinCode)}`,
      {}
    );

    if (response.status >= 500) {
      throw new Error(`Extend-shelf failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get receiving locations (bins with StcBinCatNo = 2 = "Varemottak")
   */
  async getReceivingLocations(companyNo: string, warehouseID: string): Promise<StcBinDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/stc-bins/receiving-locations/${companyNo}/${warehouseID}`
    );

    if (!response.ok) {
      throw new Error(`Failed to get receiving locations: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }
}

export const stcBinService = new StcBinService();
